/**
 * TileModeButtons — Quick tile aspect mode switcher for mobile.
 * Floating buttons on the left side: Square, Individual, Fixed presets.
 */
import React, { useState } from 'react';

interface TileModeButtonsProps {
    store: any;
}

const presets = [
    { label: '■', mode: 'square' as const, aspect: 1.0, title: 'Square' },
    { label: '▬', mode: 'fixed' as const, aspect: 16/9, title: '16:9' },
    { label: '▮', mode: 'fixed' as const, aspect: 9/16, title: '9:16' },
    { label: '▭', mode: 'fixed' as const, aspect: 4/3, title: '4:3' },
    { label: '✦', mode: 'individual' as const, aspect: 0, title: 'Original' },
];

const TileModeButtons: React.FC<TileModeButtonsProps> = ({ store }) => {
    const [expanded, setExpanded] = useState(false);
    const currentMode = store((s: any) => s.tileAspectMode);
    const currentFixed = store((s: any) => s.tileFixedAspect);

    const isActive = (p: typeof presets[0]) => {
        if (p.mode === 'square') return currentMode === 'square';
        if (p.mode === 'individual') return currentMode === 'individual';
        return currentMode === 'fixed' && Math.abs(currentFixed - p.aspect) < 0.01;
    };

    const apply = (p: typeof presets[0]) => {
        const s = store.getState();
        s.setTileAspectMode(p.mode);
        if (p.mode === 'fixed') s.setTileFixedAspect(p.aspect);
    };

    return (
        <div style={{
            position: 'fixed', left: '8px', top: '50%', transform: 'translateY(-50%)',
            zIndex: 10001, display: 'flex', flexDirection: 'column', gap: '4px',
            alignItems: 'flex-start',
        }}>
            {/* Toggle button */}
            <button
                onClick={() => setExpanded(!expanded)}
                style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: expanded ? '#6366f1' : 'rgba(15,23,42,0.85)',
                    border: '1px solid rgba(99,102,241,0.4)',
                    color: '#e2e8f0', fontSize: '14px', cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
            >
                {expanded ? '×' : '⊞'}
            </button>

            {expanded && presets.map((p, i) => (
                <button
                    key={i}
                    onClick={() => apply(p)}
                    style={{
                        width: '32px', height: '32px', borderRadius: '8px',
                        background: isActive(p) ? '#6366f1' : 'rgba(15,23,42,0.85)',
                        border: `1px solid ${isActive(p) ? '#818cf8' : 'rgba(99,102,241,0.3)'}`,
                        color: isActive(p) ? '#fff' : '#a78bfa',
                        fontSize: '14px', cursor: 'pointer', backdropFilter: 'blur(8px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        position: 'relative',
                    }}
                    title={p.title}
                >
                    {p.label}
                    <span style={{
                        position: 'absolute', left: '36px', top: '50%', transform: 'translateY(-50%)',
                        fontSize: '9px', color: '#94a3b8', whiteSpace: 'nowrap',
                        background: 'rgba(15,23,42,0.9)', padding: '2px 6px', borderRadius: '4px',
                        pointerEvents: 'none',
                    }}>
                        {p.title}
                    </span>
                </button>
            ))}
        </div>
    );
};

export default TileModeButtons;
